import { useTemplateContext } from "../provider";
import { VerificationSchemaView } from "../slices/types";
import { tabTitle } from "../utils";
import { Show } from "shared/components";
import { Tabs } from "../../../components/Tabs";
import { ApplicantType } from "shared/types";
import { ReactNode, useEffect } from "react";
import {
  basicInformationTabs,
  criminalReportTabs,
  housingHistoryTabs,
  identityVerificationTabs,
  incomeHistoryTabs,
  screeningServicesTabs
} from "../constant";
import { cx } from "../../../../utils/classnames";
import { BreadCrumbs } from "shared/components/BreadCrumbs";
import { SwitchWithReadOnlyMode } from "../SwitchWithReadOnlyMode";

const HEADER_CONTENT = {
  applicant: {
    title: "Applicant application",
    description:
      "Applicant is an individual over 18 living in the property, like a roommate, spouse, or partner."
  },
  co_signer: {
    title: "Guarantor application",
    description:
      "Guarantor is someone who won’t live in the property but will provide financial support."
  },
  terms_and_conditions: {
    title: tabTitle[VerificationSchemaView.TenantQualifications],
    description:
      "Specify any resident qualifications or information for applicants and guarantors to see on the first page."
  },
  submission_disclosure: {
    title: tabTitle[VerificationSchemaView.SubmissionDisclosure],
    description:
      "Is there specific information for the applicant or guarantor? It will appear before submitting the application."
  }
};

const getHeader = (
  verificationType: VerificationSchemaView,
  applicantType: ApplicantType
) => {
  if (verificationType === VerificationSchemaView.ScreeningServices)
    return HEADER_CONTENT[applicantType];

  return (
    HEADER_CONTENT[verificationType] || {
      title: basicInformationTabs.includes(verificationType)
        ? "Basic Information"
        : incomeHistoryTabs.includes(verificationType)
          ? "Income History"
          : housingHistoryTabs.includes(verificationType)
            ? "Housing History"
            : tabTitle[verificationType],
      description: ""
    }
  );
};

export const LayoutHeader = () => {
  const {
    partnerType,
    selectedVerificationSchema,
    setSelectedVerificationSchema,
    setSelectedStep,
    isModal,
    template,
    savedTemplate,
    setTemplate,
    readOnly
  } = useTemplateContext();
  const { title, description } = getHeader(
    selectedVerificationSchema,
    partnerType
  );

  const getDisplayMap = (
    tabs: VerificationSchemaView[],
    defaultLabel: VerificationSchemaView | null
  ): Record<VerificationSchemaView, ReactNode> =>
    tabs.reduce<Record<VerificationSchemaView, ReactNode>>(
      (acc, schema) => ({
        ...acc,
        [schema]: schema === defaultLabel ? "General" : tabTitle[schema]
      }),
      {} as Record<VerificationSchemaView, ReactNode>
    );

  const tabSections = [
    { tabs: basicInformationTabs, label: null, setActiveTab: setSelectedStep },
    {
      tabs: identityVerificationTabs,
      label: VerificationSchemaView.IdentityVerification
    },
    {
      tabs: incomeHistoryTabs,
      label: VerificationSchemaView.IncomeHistory,
      setActiveTab: setSelectedStep,
      startedTab: 3
    },
    { tabs: housingHistoryTabs, label: VerificationSchemaView.HousingHistory },
    { tabs: criminalReportTabs, label: VerificationSchemaView.CriminalReport }
  ];

  useEffect(() => {
    // Reset step when switching to Screening Services
    if (selectedVerificationSchema === VerificationSchemaView.ScreeningServices)
      setSelectedStep(0);
  }, [selectedVerificationSchema]);

  const toggleCoSignerEnabled = () => {
    if (!template || !savedTemplate) return;

    setTemplate({
      ...template,
      settings: {
        ...template.settings,
        co_signer: {
          ...template.settings.co_signer,
          enabled_screening_services:
            !template.settings.co_signer.enabled_screening_services
        }
      },
      verification_schemas: {
        ...template.verification_schemas,
        co_signer: {
          ...savedTemplate.verification_schemas.applicant
        }
      }
    });
  };

  const shouldShowBreadcrumbs =
    selectedVerificationSchema === VerificationSchemaView.CreditReport ||
    screeningServicesTabs.includes(selectedVerificationSchema);

  return (
    <div
      className={cx(
        screeningServicesTabs.includes(selectedVerificationSchema)
          ? ""
          : "border-b border-alpha-100",
        "w-full"
      )}
    >
      <Show if={shouldShowBreadcrumbs}>
        <div className="px-6 py-[22px] border-b border-alpha-40 bg-background-1000">
          <BreadCrumbs
            crumbs={[
              {
                title: `${
                  partnerType === ApplicantType.Applicant
                    ? "Applicant"
                    : "Guarantor"
                } application`,
                onClick: () =>
                  setSelectedVerificationSchema(
                    VerificationSchemaView.ScreeningServices
                  )
              },
              {
                title:
                  getHeader(selectedVerificationSchema, partnerType)?.title ||
                  tabTitle[selectedVerificationSchema],
                isActive: true,
                onClick: () =>
                  setSelectedVerificationSchema(selectedVerificationSchema)
              }
            ]}
          />
        </div>
      </Show>

      <div className="bg-background-1000 md:px-6 px-3">
        <div className="md:py-6 py-3">
          <div className="flex justify-between items-center">
            <div className="space-y-2">
              <h3 className="font-bold text-xl">{title}</h3>
              {description ? (
                <p className="text-content-400 text-base">{description}</p>
              ) : null}
            </div>

            <Show
              if={
                selectedVerificationSchema ===
                  VerificationSchemaView.ScreeningServices &&
                partnerType === ApplicantType.CoSigner
              }
            >
              <SwitchWithReadOnlyMode
                label="Allow guarantors"
                checked={Boolean(
                  template?.settings.co_signer.enabled_screening_services
                )}
                onChange={() => toggleCoSignerEnabled()}
                readOnly={readOnly}
              />
            </Show>
          </div>
        </div>

        <div
          className={cx(
            isModal ? "max-w-[calc(90vw-312px)]" : "max-w-[calc(100vw-392px)]"
          )}
        >
          {tabSections.map(
            ({
              tabs,
              label,
              setActiveTab = setSelectedVerificationSchema,
              startedTab
            }) => (
              <Show
                key={tabs.join(",")}
                if={tabs.includes(selectedVerificationSchema)}
              >
                <Tabs
                  variant="underline"
                  tabs={tabs}
                  displayMap={getDisplayMap(tabs, label)}
                  selected={selectedVerificationSchema}
                  setActiveTab={(t) => {
                    if (
                      setActiveTab === setSelectedStep &&
                      t !== VerificationSchemaView.AdditionalQuestions
                    )
                      setSelectedStep(
                        tabs.findIndex((schema) => schema === t) -
                          (startedTab ?? 0)
                      );

                    setSelectedVerificationSchema(t as VerificationSchemaView);
                  }}
                />
              </Show>
            )
          )}
        </div>
      </div>
    </div>
  );
};
